import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import DownloadModal from "@/components/base/DownloadModal";
import EmptyState from "@/components/base/EmptyState";
import usePageMeta from "@/hooks/usePageMeta";
import { getAppById, watchApps } from "@/mocks/movies";

const Stars = ({ score }: { score: number }) => (
  <span className="flex items-center gap-0.5 text-accent-500 text-xs">
    {[1, 2, 3, 4, 5].map((item) => (
      <i key={item} className={item <= Math.round(score) ? "ri-star-fill" : "ri-star-line text-foreground-300"}></i>
    ))}
  </span>
);

export default function AppDetail() {
  const params = useParams<{ id: string }>();
  const app = getAppById(params.id ?? "");
  const [downloadOpen, setDownloadOpen] = useState(false);

  usePageMeta({
    title: app ? `${app.name} - 看片APP详情 - 看片免费app网站` : "应用信息暂不可用 - 看片免费app网站",
    description: app
      ? `${app.name}（${app.version}）：${app.summary} 支持 ${app.platforms.join("、")}，查看功能介绍、更新日志与用户评价。`
      : "该应用信息暂不可用，可能已下架或链接有误。",
    keywords: app
      ? `${app.name},${app.name}下载,看片APP,${app.category}看片app,${app.platforms.join(",")}`
      : "看片APP,应用推荐",
  });

  const related = useMemo(() => {
    if (!app) return [];
    return watchApps
      .filter((item) => item.id !== app.id)
      .sort((a, b) => {
        const scoreA = (a.category === app.category ? 2 : 0) + a.platforms.filter((p) => app.platforms.includes(p)).length;
        const scoreB = (b.category === app.category ? 2 : 0) + b.platforms.filter((p) => app.platforms.includes(p)).length;
        return scoreB - scoreA || b.rating - a.rating;
      })
      .slice(0, 3);
  }, [app]);

  if (!app) {
    return (
      <SiteLayout>
        <div className="w-full max-w-page mx-auto px-4 md:px-8 py-10">
          <EmptyState
            icon="ri-apps-2-line"
            title="该应用信息暂不可用"
            description="应用可能已下架或链接有误。你可以回到应用推荐页，查看当前收录的其他看片工具。"
            primaryLabel="返回应用推荐"
            primaryTo="/app"
            secondaryLabel="返回首页"
          />
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="w-full bg-primary-950">
        <div className="w-full max-w-page mx-auto px-4 md:px-8 py-7 md:py-10">
          <Breadcrumb
            dark
            items={[
              { label: "首页", to: "/" },
              { label: "看片APP推荐", to: "/app" },
              { label: app.name },
            ]}
          />
          <div className="mt-6 flex flex-col sm:flex-row gap-5 md:gap-7">
            <div className="w-[100px] h-[100px] md:w-[128px] md:h-[128px] rounded-lg overflow-hidden border border-primary-700/70 bg-primary-900 shrink-0">
              <img
                src={app.icon}
                alt={`${app.name}应用图标`}
                title={`${app.name} - 看片APP下载`}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-2 py-0.5 rounded bg-accent-500 text-primary-950 text-[11px] font-medium">
                  {app.category}
                </span>
                <span className="px-2 py-0.5 rounded border border-primary-700 text-primary-200 text-[11px]">
                  {app.version}
                </span>
                <span className="px-2 py-0.5 rounded border border-primary-700 text-primary-200 text-[11px]">
                  {app.size}
                </span>
              </div>
              <h1 className="mt-3 text-2xl md:text-3xl font-bold text-background-50">{app.name}</h1>
              <p className="mt-2 text-sm text-primary-200 max-w-2xl leading-6">{app.summary}</p>

              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-primary-300">
                <span className="flex items-center gap-1 font-label">
                  <i className="ri-star-fill text-accent-400"></i>
                  {app.rating.toFixed(1)} 分
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-download-2-line"></i>
                  {app.downloads}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  更新于 {app.updated}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-device-line"></i>
                  {app.platforms.join(" / ")}
                </span>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-2.5">
                <button
                  type="button"
                  onClick={() => setDownloadOpen(true)}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-medium whitespace-nowrap hover:bg-accent-400 transition-colors cursor-pointer"
                >
                  <i className="ri-download-cloud-2-line"></i>
                  立即下载
                </button>
                <Link
                  to="/app"
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md border border-primary-600 text-primary-100 text-sm whitespace-nowrap hover:border-accent-400 hover:text-accent-300 transition-colors"
                >
                  更多应用
                </Link>
                <span className="text-[11px] text-primary-400">下载功能为前端演示状态</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full max-w-page mx-auto px-4 md:px-8 mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-8 space-y-6">
            <section className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
              <h2 className="text-lg font-bold text-foreground-900">应用介绍</h2>
              <p className="mt-3 text-sm leading-7 text-foreground-700">{app.description}</p>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {app.features.map((feature) => (
                  <div key={feature} className="flex items-start gap-2 text-sm text-foreground-700">
                    <i className="ri-checkbox-circle-fill text-accent-500 mt-0.5"></i>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-lg font-bold text-foreground-900">版本信息与更新日志</h2>
                <span className="text-xs text-foreground-500">当前版本 {app.version}</span>
              </div>
              <ol className="mt-4 space-y-4">
                {app.changelog.map((log, index) => (
                  <li key={log.version} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <span
                        className={`w-2.5 h-2.5 rounded-full mt-1.5 ${
                          index === 0 ? "bg-accent-500" : "bg-background-300"
                        }`}
                      ></span>
                      {index < app.changelog.length - 1 && <span className="w-px flex-1 bg-background-200 mt-1"></span>}
                    </div>
                    <div className="pb-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-foreground-900">{log.version}</span>
                        <span className="text-[11px] text-foreground-400">{log.date}</span>
                      </div>
                      <p className="mt-1 text-xs leading-6 text-foreground-600">{log.note}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            <section className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-lg font-bold text-foreground-900">用户使用评价</h2>
                <span className="text-xs text-foreground-400">以下为演示评价数据</span>
              </div>
              <ul className="mt-4 space-y-4">
                {app.reviews.map((review) => (
                  <li key={review.user} className="rounded-md bg-background-100/70 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                          {review.user.slice(0, 1)}
                        </span>
                        <span className="text-sm font-medium text-foreground-800">{review.user}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Stars score={review.score} />
                        <span className="text-[11px] text-foreground-400">{review.date}</span>
                      </div>
                    </div>
                    <p className="mt-2 text-sm leading-6 text-foreground-700">{review.text}</p>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <aside className="lg:col-span-4 space-y-4">
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h2 className="text-base font-semibold text-foreground-900">基本信息</h2>
              <dl className="mt-4 space-y-3 text-xs">
                {[
                  { label: "应用名称", value: app.name },
                  { label: "收费方式", value: app.category },
                  { label: "安装大小", value: app.size },
                  { label: "当前版本", value: app.version },
                  { label: "更新时间", value: app.updated },
                  { label: "支持平台", value: app.platforms.join(" / ") },
                  { label: "累计下载", value: app.downloads },
                ].map((row) => (
                  <div key={row.label} className="flex gap-3">
                    <dt className="w-16 shrink-0 text-foreground-400">{row.label}</dt>
                    <dd className="flex-1 text-foreground-800 leading-5">{row.value}</dd>
                  </div>
                ))}
              </dl>
              <button
                type="button"
                onClick={() => setDownloadOpen(true)}
                className="mt-5 w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-download-2-line"></i>
                下载应用（演示）
              </button>
            </div>

            <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-5">
              <h2 className="text-base font-semibold text-secondary-900">相关推荐APP</h2>
              <ul className="mt-3 space-y-3">
                {related.map((item) => (
                  <li key={item.id}>
                    <Link to={`/app/detail/${item.id}`} className="flex items-center gap-3 group">
                      <div className="w-10 h-10 rounded-md overflow-hidden bg-primary-900 shrink-0">
                        <img
                          src={item.icon}
                          alt={`${item.name}图标`}
                          title={`${item.name} - 看片应用`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
                          {item.name}
                        </p>
                        <p className="text-[11px] text-foreground-500 truncate">{item.category} · {item.size}</p>
                      </div>
                      <i className="ri-arrow-right-s-line text-foreground-300"></i>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
              <p className="text-sm font-medium text-accent-950">下载提示</p>
              <p className="mt-1.5 text-xs leading-6 text-accent-900">
                当前应用下载链接尚未接入真实分发渠道，页面按钮仅用于演示交互。正式上线后可直接跳转应用商店。
              </p>
            </div>
          </aside>
        </div>
      </div>

      <DownloadModal
        open={downloadOpen}
        appName={app.name}
        onClose={() => {
          setDownloadOpen(false);
        }}
      />
    </SiteLayout>
  );
}